import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { FeatureRadio } from '@/components/chariot-ui';
import { Input, InputEvent } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useModifyAccount } from '@/hooks/useAccounts';
import useInitialize, {
  CloudInitializeRequest,
  CloudInitializeResponse,
} from '@/hooks/useInitialize';
import { triggerConfetti } from '@/utils/confetti.util';
import { downloadIACTemplate } from '@/utils/download.util';
import { useStorage } from '@/utils/storage/useStorage.util';

const DEV_TYPE_OPTIONS = [
  { label: 'Terraform (Recommended)', value: 'terraform' },
  { label: 'Manual', value: 'manual' },
];

const defaultFormState = {
  allSubscriptions: { tenant_id: '', deployment_type: '' },
  individual: { tenant_id: '', subscription_id: '', deployment_type: '' },
};

type SelectionType = 'allSubscriptions' | 'individual';

type FormState = {
  allSubscriptions: { tenant_id: string; deployment_type: string };
  individual: {
    tenant_id: string;
    subscription_id: string;
    deployment_type: string;
  };
};

export function Azure({ onClose }: { onClose: () => void }) {
  const [savedState, setSavedState] = useStorage<{
    request: CloudInitializeRequest;
    response: CloudInitializeResponse;
  }>({ key: 'azure-integration-state' });

  const [selection, setSelection] = useState<SelectionType | undefined>(
    savedState
      ? savedState?.request.subscription_id
        ? 'individual'
        : 'allSubscriptions'
      : 'allSubscriptions'
  );
  const [form, setForm] = useState<FormState>(
    savedState
      ? {
          allSubscriptions: {
            tenant_id: savedState?.request.tenant_id || '',
            deployment_type: savedState?.request.deployment_type || '',
          },
          individual: {
            tenant_id: savedState?.request.tenant_id || '',
            subscription_id: savedState?.request.subscription_id || '',
            deployment_type: savedState?.request.deployment_type || '',
          },
        }
      : defaultFormState
  );
  const [step, setStep] = useState(savedState ? 1 : 0);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [clientId, setClientId] = useState('');
  const [credentialError, setCredentialError] = useState<string | null>(null);

  const { mutateAsync: initializeCloud, status: initializeCloudStatus } =
    useInitialize();
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');

  // Trigger confetti effect when entering step 2
  useEffect(() => {
    if (step === 2) {
      triggerConfetti({ delay: 300 });
    }
  }, [step]);

  // Handle input changes
  const handleInputChange =
    (type: SelectionType, field: string) => (e: InputEvent) => {
      // Clear error when user starts typing again
      if (credentialError) {
        setCredentialError(null);
      }

      setForm(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          [field]: e.target.value,
        },
      }));
    };

  // Handle Dropdown selection for deployment_type
  const handleDropdownSelect =
    (type: SelectionType) => (option: { value?: string | number }) => {
      // Clear error when user changes selections
      if (credentialError) {
        setCredentialError(null);
      }

      if (typeof option.value === 'string') {
        setForm(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            deployment_type: option.value,
          },
        }));
      }
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selection) return;

    const request: CloudInitializeRequest = {
      provider: 'azure',
      tenant_id: form[selection].tenant_id,
      deployment_type: form[selection].deployment_type,
      ...(selection === 'individual' && {
        subscription_id: form.individual.subscription_id,
      }),
    };

    const response = await initializeCloud(request);
    setSavedState({
      request,
      response,
    });
    setStep(1);
  };

  // Find the selected deployment_type option for Dropdown
  const getSelectedDropdownOption = (type: SelectionType) => {
    const val = form[type].deployment_type;
    return val ? [DEV_TYPE_OPTIONS.find(opt => opt.value === val)!] : [];
  };

  const handleReset = () => {
    setSavedState(undefined);
    setSelection(undefined);
    setHasDownloaded(false);
    setForm(defaultFormState);
    setStep(0);
    setCredentialError(null);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleFinish = async () => {
    if (form[selection!].deployment_type !== 'manual' && !hasDownloaded) {
      if (savedState?.response?.template) {
        const point =
          selection === 'allSubscriptions'
            ? form.allSubscriptions.tenant_id
            : form.individual.subscription_id;

        downloadIACTemplate({
          template: savedState.response.template,
          filename: `guard-azure-${point}`,
          deploymentType: form[selection!].deployment_type,
        });
        setHasDownloaded(true);
      }
    } else {
      const tenant_id = form[selection!].tenant_id;

      try {
        await link({
          username: 'azure',
          config: {
            tenant_id,
            cognito_username: savedState?.response.username,
            cognito_password: savedState?.response.password,
            azure_enterprise_app_id: clientId,
            ...(selection === 'individual' && {
              subscription_id: form.individual.subscription_id,
            }),
          },
          value:
            selection === 'individual'
              ? form.individual.subscription_id
              : tenant_id,
        });

        setSavedState(undefined);
        setStep(2);
      } catch (error: unknown) {
        const errorMessage =
          typeof error === 'string'
            ? error
            : 'Failed to connect Azure integration';
        setCredentialError(errorMessage);

        // Reset to step 0 but preserve the error state
        setSavedState(undefined);
        setSelection(undefined);
        setHasDownloaded(false);
        setForm(defaultFormState);
        setStep(0);
      }
    }
  };

  return (
    <div className="">
      {credentialError ? (
        <div
          role="alert"
          aria-live="assertive"
          className="bg-chariot-status-critical/10 text-chariot-text-primary -mt-4 mb-8 flex items-start gap-3 rounded-md p-3 text-sm"
        >
          <ExclamationTriangleIcon className="text-chariot-stroke-critical mt-0.5 size-4 shrink-0" />
          <div className="w-full">
            <div className="mb-1 flex items-start justify-between gap-2">
              <p className="text-chariot-text-primary font-medium">
                Invalid Credentials
              </p>
            </div>
            <p className="text-chariot-text-secondary">
              The Azure integration credentials you provided are invalid or
              expired. Please verify your credentials and try again.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-chariot-stroke mb-6 h-[2px] w-full shrink-0 rounded-lg" />
      )}

      {step === 0 && (
        <>
          <div className="text-chariot-text-primary mb-4 font-bold">
            Scope of Integration
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <FeatureRadio
              id="azure-radio-all-subscriptions"
              title="All Subscriptions"
              description="Integrates all accounts within the organization"
              checked={selection === 'allSubscriptions'}
              onChange={() => {
                if (credentialError) {
                  setCredentialError(null);
                }
                setSelection('allSubscriptions');
              }}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Tenant ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="tenantId"
                      value={form.allSubscriptions.tenant_id}
                      onChange={handleInputChange(
                        'allSubscriptions',
                        'tenant_id'
                      )}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Deployment Type
                    </label>
                    <Select
                      value={
                        (getSelectedDropdownOption('allSubscriptions')[0]
                          ?.value as string | undefined) ?? ''
                      }
                      onValueChange={value =>
                        handleDropdownSelect('allSubscriptions')({ value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select deployment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEV_TYPE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              }
            />
            <FeatureRadio
              id="azure-radio-individual"
              title="Individual Subscription"
              description="Integrates a single account"
              checked={selection === 'individual'}
              onChange={() => {
                if (credentialError) {
                  setCredentialError(null);
                }
                setSelection('individual');
              }}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Tenant ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="tenantId"
                      value={form.individual.tenant_id}
                      onChange={handleInputChange('individual', 'tenant_id')}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Subscription ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="subscriptionId"
                      value={form.individual.subscription_id}
                      onChange={handleInputChange(
                        'individual',
                        'subscription_id'
                      )}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Deployment Type
                    </label>
                    <Select
                      value={
                        (getSelectedDropdownOption('individual')[0]?.value as
                          | string
                          | undefined) ?? ''
                      }
                      onValueChange={value =>
                        handleDropdownSelect('individual')({ value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select deployment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEV_TYPE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              }
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="px-6"
                onClick={handleCancel}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !selection ||
                  !form[selection].tenant_id ||
                  (selection === 'individual' &&
                    !form.individual.subscription_id) ||
                  !form[selection].deployment_type ||
                  initializeCloudStatus === 'pending'
                }
                className="px-6"
                size="sm"
              >
                Next
              </Button>
            </div>
          </form>
        </>
      )}
      {step === 1 && (
        <div>
          {form[selection!].deployment_type !== 'manual' ? (
            <>
              {!hasDownloaded && (
                <>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="font-bold">Download Template</div>
                  </div>
                  <div className="text-chariot-text-secondary mb-6 text-sm">
                    To finish Azure Terraform setup, please download the IAC
                    Template, then click finish. For more information, please
                    reference our{' '}
                    <a
                      href="https://docs.praetorian.com/articles/37956598833563-microsoft-azure-iac-deployment-recommended"
                      className="text-chariot-brand-primary hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Azure documentation
                    </a>
                    .
                  </div>
                </>
              )}
              {hasDownloaded && (
                <>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="font-bold">Verify Deployment</div>
                  </div>
                  <div className="text-chariot-text-secondary mb-6 text-sm">
                    To verify deployment, please enter the application client ID
                    found from the IAC deployment output. For more information,
                    please reference our{' '}
                    <a
                      href="https://docs.praetorian.com/articles/37956598833563-microsoft-azure-iac-deployment-recommended"
                      className="text-chariot-brand-primary hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Azure documentation
                    </a>
                    .
                  </div>
                  <div className="mb-6">
                    <label className="mb-2 block text-sm font-bold">
                      Application Client ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="clientId"
                      value={clientId}
                      onChange={e => setClientId(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <div>
              <div className="mb-4 font-bold">Manual Setup</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                For manual setup, please follow our{' '}
                <a
                  href="https://docs.praetorian.com/articles/37956568460187-microsoft-azure-manual-deployment"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Azure documentation
                </a>{' '}
                to configure your Azure environment.
              </div>
              {savedState?.response?.username && (
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold">
                    Subject
                  </label>
                  <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3">
                    <code className="text-chariot-text-primary text-sm">
                      {savedState.response.username}
                    </code>
                  </div>
                </div>
              )}
              <div className="mb-6">
                <label className="mb-2 block text-sm font-bold">
                  Application Client ID
                </label>
                <Input
                  className="bg-transparent"
                  name="clientId"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              className="px-4"
              type="button"
              disabled={
                form[selection!].deployment_type !== 'manual' && hasDownloaded
                  ? !clientId || linkStatus === 'pending'
                  : linkStatus === 'pending'
              }
              onClick={handleFinish}
              size="sm"
            >
              {form[selection!].deployment_type !== 'manual' && !hasDownloaded
                ? 'Download IAC Template'
                : 'Finish'}
            </Button>
          </div>
        </div>
      )}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-chariot-text-primary mb-2 text-2xl font-bold"
          >
            Hooray! You&apos;re All Set!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-chariot-text-secondary mb-8 text-sm"
          >
            Your Azure setup is complete. Chariot will now scan and report on
            this environment
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Button
              onClick={() => {
                handleCancel();
              }}
              size="sm"
              className="px-8"
            >
              Done
            </Button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
